import { z } from 'zod';

// ============================================================================
// Configuration Schemas
// ============================================================================

// Schema for .smartstack/config.json (created by CLI during ss init)
export const ProjectConfigSchema = z.object({
  projectType: z.enum(['client', 'platform']).default('platform'),
  dbContext: z.enum(['core', 'extensions']).default('core'),
  baseNamespace: z.string().optional(),
  smartStackVersion: z.string().optional(),
  initialized: z.string().optional(),
});

export type ProjectConfig = z.infer<typeof ProjectConfigSchema>;

export const SmartStackConfigSchema = z.object({
  projectPath: z.string(),
  apiUrl: z.string().url().optional(),
  apiEnabled: z.boolean().default(true),
});

export const ConventionsConfigSchema = z.object({
  schemas: z.object({
    platform: z.string().default('core'),
    extensions: z.string().default('extensions'),
  }),
  tablePrefixes: z.array(z.string()).default([
    'auth_', 'nav_', 'usr_', 'ai_', 'cfg_', 'wkf_',
    'support_', 'entra_', 'ref_', 'loc_', 'lic_'
  ]),
  scopeTypes: z.array(z.string()).default(['Core', 'Extension', 'Partner', 'Community']),
  migrationFormat: z.string().default('{context}_v{version}_{sequence}_{Description}'),
  namespaces: z.object({
    domain: z.string(),
    application: z.string(),
    infrastructure: z.string(),
    api: z.string(),
  }),
  servicePattern: z.object({
    interface: z.string().default('I{Name}Service'),
    implementation: z.string().default('{Name}Service'),
  }),
});

export const EfCoreContextSchema = z.object({
  name: z.string(),
  projectPath: z.string(),
  migrationsFolder: z.string().default('Migrations'),
});

export const EfCoreConfigSchema = z.object({
  contexts: z.array(EfCoreContextSchema),
  validation: z.object({
    checkModelSnapshot: z.boolean().default(true),
    checkMigrationOrder: z.boolean().default(true),
    requireBuildSuccess: z.boolean().default(true),
  }),
});

export const ScaffoldingConfigSchema = z.object({
  outputPath: z.string(),
  templates: z.object({
    service: z.string(),
    entity: z.string(),
    controller: z.string(),
    component: z.string(),
  }),
});

export const ConfigSchema = z.object({
  version: z.string(),
  smartstack: SmartStackConfigSchema,
  conventions: ConventionsConfigSchema,
  efcore: EfCoreConfigSchema,
  scaffolding: ScaffoldingConfigSchema,
  // Loaded from .smartstack/config.json if present
  projectConfig: ProjectConfigSchema.optional(),
  // Resolved DbContext to use (from projectConfig or default)
  defaultDbContext: z.enum(['core', 'extensions']).default('core'),
});

export type Config = z.infer<typeof ConfigSchema>;
export type SmartStackConfig = z.infer<typeof SmartStackConfigSchema>;
export type ConventionsConfig = z.infer<typeof ConventionsConfigSchema>;
export type EfCoreConfig = z.infer<typeof EfCoreConfigSchema>;

// ============================================================================
// Tool Input Schemas
// ============================================================================

export const ValidateConventionsInputSchema = z.object({
  path: z.string().optional().describe('Project path to validate (default: SmartStack.app path)'),
  checks: z.array(z.enum(['tables', 'migrations', 'services', 'namespaces', 'entities', 'tenants', 'controllers', 'layouts', 'tabs', 'hierarchies', 'protected-actions', 'all']))
    .default(['all'])
    .describe('Types of checks to perform'),
});

export const CheckMigrationsInputSchema = z.object({
  projectPath: z.string().optional().describe('EF Core project path'),
  branch: z.string().optional().describe('Git branch to check (default: current)'),
  compareBranch: z.string().optional().describe('Branch to compare against'),
});

export const EntityPropertySchema = z.object({
  name: z.string().describe('Property name (PascalCase)'),
  type: z.string().describe('C# type (string, int, Guid, DateTime, etc.)'),
  required: z.boolean().optional().describe('If true, property is required'),
  maxLength: z.number().optional().describe('Max length for string properties'),
});

export const ScaffoldExtensionInputSchema = z.object({
  type: z.enum(['feature', 'service', 'entity', 'controller', 'component', 'test', 'dto', 'validator', 'repository'])
    .describe('Type of extension to scaffold. Use "feature" for full-stack generation.'),
  name: z.string().describe('Name of the extension (e.g., "UserProfile", "Order")'),
  options: z.object({
    namespace: z.string().optional().describe('Custom namespace'),
    baseEntity: z.string().optional().describe('Base entity to extend (for entity type)'),
    methods: z.array(z.string()).optional().describe('Methods to generate (for service type)'),
    outputPath: z.string().optional().describe('Custom output path'),
    isSystemEntity: z.boolean().optional().describe('If true, creates a system entity without TenantId'),
    tablePrefix: z.string().optional().describe('Domain prefix for table name (e.g., "auth_", "nav_", "cfg_")'),
    schema: z.enum(['core', 'extensions']).optional().describe('Database schema (default: core)'),
    dryRun: z.boolean().optional().describe('If true, preview generated code without writing files'),
    skipService: z.boolean().optional().describe('For feature type: skip service generation'),
    skipController: z.boolean().optional().describe('For feature type: skip controller generation'),
    skipComponent: z.boolean().optional().describe('For feature type: skip React component generation'),
    clientExtension: z.boolean().optional().describe('If true, use extensions schema for client-specific code'),
    withTests: z.boolean().optional().describe('For feature type: also generate unit tests'),
    withDtos: z.boolean().optional().describe('For feature type: generate DTOs (Create, Update, Response)'),
    withValidation: z.boolean().optional().describe('For feature type: generate FluentValidation validators'),
    withRepository: z.boolean().optional().describe('For feature type: generate repository pattern'),
    entityProperties: z.array(EntityPropertySchema).optional().describe('Entity properties for DTO/Validator generation'),
    navRoute: z.string().optional().describe('Navigation route path for controller (e.g., "platform.administration.users"). Required for controllers.'),
    navRouteSuffix: z.string().optional().describe('Optional suffix for NavRoute (e.g., "dashboard" for sub-resources)'),
    withHierarchyFunction: z.boolean().optional().describe('For entity type with self-reference (ParentId): generate TVF SQL script for hierarchy traversal'),
    hierarchyDirection: z.enum(['ancestors', 'descendants', 'both']).optional().describe('Direction for hierarchy traversal function (default: both)'),
  }).optional(),
});

export const ApiDocsInputSchema = z.object({
  endpoint: z.string().optional().describe('Filter by endpoint path (e.g., "/api/users")'),
  format: z.enum(['markdown', 'json', 'openapi']).default('markdown')
    .describe('Output format'),
  controller: z.string().optional().describe('Filter by controller name'),
});

export const SuggestMigrationInputSchema = z.object({
  description: z.string().describe('Description of what the migration does (e.g., "Add User Profiles", "Create Orders Table")'),
  context: z.enum(['core', 'extensions']).optional().describe('DbContext name (default: core)'),
  version: z.string().optional().describe('Semver version (e.g., "1.0.0", "1.2.0"). If not provided, uses latest from existing migrations.'),
});

export const GeneratePermissionsInputSchema = z.object({
  navRoute: z.string().optional().describe('NavRoute path (e.g., "platform.administration.entra"). If not provided, scans all controllers.'),
  actions: z.array(z.string()).optional().describe('Custom actions to generate (default: read, create, update, delete)'),
  includeStandardActions: z.boolean().default(true).describe('Include standard CRUD actions (read, create, update, delete)'),
  generateMigration: z.boolean().default(true).describe('Generate EF Core migration to seed permissions in database'),
  dryRun: z.boolean().default(false).describe('Preview without writing files or creating migration'),
});

export type ValidateConventionsInput = z.infer<typeof ValidateConventionsInputSchema>;
export type CheckMigrationsInput = z.infer<typeof CheckMigrationsInputSchema>;
export type ScaffoldExtensionInput = z.infer<typeof ScaffoldExtensionInputSchema>;
export type ApiDocsInput = z.infer<typeof ApiDocsInputSchema>;
export type SuggestMigrationInput = z.infer<typeof SuggestMigrationInputSchema>;
export type GeneratePermissionsInput = z.infer<typeof GeneratePermissionsInputSchema>;

// ============================================================================
// Test Tool Input Schemas
// ============================================================================

export const TestTypeSchema = z.enum(['unit', 'integration', 'security', 'e2e']);
export const TestTargetSchema = z.enum(['entity', 'service', 'controller', 'validator', 'repository', 'all']);

export const ScaffoldTestsInputSchema = z.object({
  target: TestTargetSchema.describe('Type of component to test'),
  name: z.string().min(1).describe('Component name (PascalCase, e.g., "User", "Order")'),
  testTypes: z.array(TestTypeSchema).default(['unit']).describe('Types of tests to generate'),
  options: z.object({
    includeEdgeCases: z.boolean().default(true).describe('Include edge case tests'),
    includeTenantIsolation: z.boolean().default(true).describe('Include tenant isolation tests'),
    includeSoftDelete: z.boolean().default(true).describe('Include soft delete tests'),
    includeAudit: z.boolean().default(true).describe('Include audit trail tests'),
    includeValidation: z.boolean().default(true).describe('Include validation tests'),
    includeAuthorization: z.boolean().default(false).describe('Include authorization tests'),
    includePerformance: z.boolean().default(false).describe('Include performance tests'),
    entityProperties: z.array(EntityPropertySchema).optional().describe('Entity properties for test generation'),
    isSystemEntity: z.boolean().default(false).describe('If true, entity has no TenantId'),
    dryRun: z.boolean().default(false).describe('Preview without writing files'),
  }).optional(),
});

export const AnalyzeTestCoverageInputSchema = z.object({
  path: z.string().optional().describe('Project path to analyze'),
  scope: z.enum(['entity', 'service', 'controller', 'all']).default('all').describe('Scope of analysis'),
  outputFormat: z.enum(['summary', 'detailed', 'json']).default('summary').describe('Output format'),
  includeRecommendations: z.boolean().default(true).describe('Include recommendations for missing tests'),
});

export const ValidateTestConventionsInputSchema = z.object({
  path: z.string().optional().describe('Project path to validate'),
  checks: z.array(z.enum(['naming', 'structure', 'patterns', 'assertions', 'mocking', 'all']))
    .default(['all'])
    .describe('Types of convention checks to perform'),
  autoFix: z.boolean().default(false).describe('Automatically fix minor issues'),
});

export const SuggestTestScenariosInputSchema = z.object({
  target: z.enum(['entity', 'service', 'controller', 'file']).describe('Type of target to analyze'),
  name: z.string().min(1).describe('Component name or file path'),
  depth: z.enum(['basic', 'comprehensive', 'security-focused']).default('comprehensive')
    .describe('Depth of analysis'),
});

export type ScaffoldTestsInput = z.infer<typeof ScaffoldTestsInputSchema>;
export type AnalyzeTestCoverageInput = z.infer<typeof AnalyzeTestCoverageInputSchema>;
export type ValidateTestConventionsInput = z.infer<typeof ValidateTestConventionsInputSchema>;
export type SuggestTestScenariosInput = z.infer<typeof SuggestTestScenariosInputSchema>;

// ============================================================================
// Security & Code Quality Tool Input Schemas
// ============================================================================

export const SecurityCheckSchema = z.enum([
  'hardcoded-secrets',
  'sql-injection',
  'tenant-isolation',
  'authorization',
  'dangerous-functions',
  'input-validation',
  'xss',
  'csrf',
  'logging-sensitive',
  'all',
]);

export const ValidateSecurityInputSchema = z.object({
  path: z.string().optional().describe('Project path to validate (default: SmartStack.app path)'),
  checks: z.array(SecurityCheckSchema)
    .default(['all'])
    .describe('Security checks to run'),
  severity: z.enum(['blocking', 'all']).optional()
    .describe('Filter results by severity'),
});

export const QualityMetricSchema = z.enum([
  'cognitive-complexity',
  'cyclomatic-complexity',
  'function-size',
  'nesting-depth',
  'parameter-count',
  'code-duplication',
  'file-size',
  'unused-members',
  'duplicated-strings',
  'unassigned-fields',
  'all',
]);

export const AnalyzeCodeQualityInputSchema = z.object({
  path: z.string().optional().describe('Project path to analyze (default: SmartStack.app path)'),
  metrics: z.array(QualityMetricSchema)
    .default(['all'])
    .describe('Metrics to analyze'),
  threshold: z.enum(['strict', 'normal', 'lenient']).default('normal')
    .describe('Threshold level for violations'),
  scope: z.enum(['changed', 'all']).default('all')
    .describe('Analyze only changed files or all'),
});

export type SecurityCheck = z.infer<typeof SecurityCheckSchema>;
export type ValidateSecurityInput = z.infer<typeof ValidateSecurityInputSchema>;
export type QualityMetric = z.infer<typeof QualityMetricSchema>;
export type AnalyzeCodeQualityInput = z.infer<typeof AnalyzeCodeQualityInputSchema>;

// ============================================================================
// Result Types
// ============================================================================

export interface ValidationResult {
  valid: boolean;
  errors: ValidationIssue[];
  warnings: ValidationIssue[];
  summary: string;
}

export interface ValidationIssue {
  type: 'error' | 'warning';
  category: string;
  message: string;
  file?: string;
  line?: number;
  suggestion?: string;
}

export interface MigrationCheckResult {
  hasConflicts: boolean;
  migrations: MigrationInfo[];
  conflicts: MigrationConflict[];
  suggestions: string[];
}

export interface MigrationInfo {
  name: string;
  context: string;      // DbContext name (core, extensions, etc.)
  version: string;      // Semver version (1.0.0, 1.2.0, etc.)
  sequence: string;     // Sequence number (001, 002, etc.)
  description: string;
  file: string;
  applied: boolean;
}

export interface MigrationConflict {
  type: 'order' | 'snapshot' | 'dependency' | 'naming';
  description: string;
  files: string[];
  resolution: string;
}

export interface ScaffoldResult {
  success: boolean;
  files: GeneratedFile[];
  instructions: string[];
}

export interface GeneratedFile {
  path: string;
  content: string;
  type: 'created' | 'modified';
}

export interface ProjectInfo {
  name: string;
  version: string;
  isGitRepo: boolean;
  currentBranch?: string;
  hasDotNet: boolean;
  hasEfCore: boolean;
  hasReact: boolean;
  csprojFiles: string[];
  dbContextName?: string;
}

// ============================================================================
// Test Tool Result Types
// ============================================================================

export interface TestCoverageResult {
  summary: {
    total: number;
    tested: number;
    coverage: number;
  };
  entities: TestCoverageCategory;
  services: TestCoverageCategory;
  controllers: TestCoverageCategory;
  missing: TestCoverageItem[];
  recommendations: string[];
}

export interface TestCoverageCategory {
  total: number;
  tested: number;
  coverage: number;
  items: string[];
  missingTests: string[];
}

export interface TestCoverageItem {
  name: string;
  type: 'entity' | 'service' | 'controller' | 'validator' | 'repository';
  priority: 'critical' | 'high' | 'medium' | 'low';
  recommendation: string;
  suggestedTestFile: string;
}

export interface TestConventionResult {
  valid: boolean;
  violations: TestConventionViolation[];
  suggestions: string[];
  autoFixedCount: number;
}

export interface TestConventionViolation {
  type: 'naming' | 'structure' | 'pattern' | 'assertion' | 'mocking';
  severity: 'error' | 'warning';
  file: string;
  line?: number;
  message: string;
  suggestion: string;
  autoFixable: boolean;
}

export interface TestScenarioResult {
  target: string;
  targetType: 'entity' | 'service' | 'controller' | 'file';
  detectedMethods: DetectedMethod[];
  scenarios: TestScenario[];
  coverage: {
    happyPath: number;
    validation: number;
    errorHandling: number;
    security: number;
    edgeCases: number;
  };
}

export interface DetectedMethod {
  name: string;
  returnType: string;
  parameters: string[];
  isAsync: boolean;
  visibility: 'public' | 'protected' | 'private' | 'internal';
}

export interface TestScenario {
  methodName: string;
  scenarioName: string;
  description: string;
  type: 'happy-path' | 'validation' | 'error-handling' | 'security' | 'edge-case' | 'performance';
  priority: 'critical' | 'high' | 'medium' | 'low';
  testMethodName: string;
  inputs?: string[];
  expectedOutput?: string;
  assertions: string[];
}

// ============================================================================
// Security & Code Quality Tool Result Types
// ============================================================================

export interface SecurityValidationResult {
  valid: boolean;
  summary: string;
  findings: SecurityFinding[];
  stats: {
    blocking: number;
    critical: number;
    warning: number;
    filesScanned: number;
  };
}

export interface SecurityFinding {
  severity: 'blocking' | 'critical' | 'warning';
  category: string;
  message: string;
  file: string;
  line?: number;
  code?: string;
  suggestion: string;
  cweId?: string;
}

export interface CodeQualityResult {
  summary: QualitySummary;
  metrics: MetricResults;
  hotspots: Hotspot[];
}

export interface QualitySummary {
  score: number;
  grade: 'A' | 'B' | 'C' | 'D' | 'F';
  filesAnalyzed: number;
  functionsAnalyzed: number;
  issuesFound: number;
}

export interface MetricResults {
  cognitiveComplexity: MetricStat;
  cyclomaticComplexity: MetricStat;
  functionSize: MetricStat;
  nestingDepth: MetricStat;
  fileSize: MetricStat;
}

export interface MetricStat {
  average: number;
  max: number;
  threshold: number;
  violations: number;
}

export interface Hotspot {
  file: string;
  function?: string;
  issues: string[];
  severity: 'high' | 'medium' | 'low';
  metrics: {
    cognitiveComplexity?: number;
    cyclomaticComplexity?: number;
    lineCount?: number;
    nestingDepth?: number;
  };
}

export interface FunctionMetrics {
  name: string;
  file: string;
  startLine: number;
  endLine: number;
  lineCount: number;
  parameterCount: number;
  maxNestingDepth: number;
  cognitiveComplexity: number;
  cyclomaticComplexity: number;
}

// SonarCloud-inspired detection types (S1144, S1192, S3459)
export interface UnusedMember {
  name: string;
  type: 'method' | 'field' | 'property';
  visibility: 'private' | 'internal';
  file: string;
  line: number;
  rule: 'S1144';
}

export interface DuplicatedString {
  literal: string;
  occurrences: number;
  locations: Array<{ file: string; line: number }>;
  suggestedConstantName: string;
  rule: 'S1192';
}

export interface UnassignedField {
  name: string;
  type: string;
  file: string;
  line: number;
  rule: 'S3459';
}

export interface ExtendedCodeQualityResult {
  summary: QualitySummary;
  metrics: MetricResults;
  hotspots: Hotspot[];
  // New SonarCloud-inspired detections
  unusedMembers: UnusedMember[];
  duplicatedStrings: DuplicatedString[];
  unassignedFields: UnassignedField[];
}

// ============================================================================
// Frontend Route Tool Input Schemas
// ============================================================================

export const ScaffoldApiClientInputSchema = z.object({
  path: z.string().optional().describe('Path to SmartStack project root (defaults to configured project path)'),
  navRoute: z.string().min(1).describe('NavRoute path (e.g., "platform.administration.users")'),
  name: z.string().min(1).describe('Entity name in PascalCase (e.g., "User", "Order")'),
  methods: z.array(z.enum(['getAll', 'getById', 'create', 'update', 'delete', 'search', 'export']))
    .default(['getAll', 'getById', 'create', 'update', 'delete'])
    .describe('API methods to generate'),
  options: z.object({
    outputPath: z.string().optional().describe('Custom output path for generated files'),
    includeTypes: z.boolean().default(true).describe('Generate TypeScript types'),
    includeHook: z.boolean().default(true).describe('Generate React Query hook'),
    dryRun: z.boolean().default(false).describe('Preview without writing files'),
  }).optional(),
});

export const ScaffoldRoutesInputSchema = z.object({
  path: z.string().optional().describe('Path to SmartStack project root (defaults to configured project path)'),
  source: z.enum(['controllers', 'navigation', 'manual'])
    .default('controllers')
    .describe('Source for route discovery: controllers (scan NavRoute attributes), navigation (from DB), manual (from config)'),
  scope: z.enum(['all', 'platform', 'business', 'extensions'])
    .default('all')
    .describe('Scope of routes to generate'),
  options: z.object({
    outputPath: z.string().optional().describe('Custom output path'),
    includeLayouts: z.boolean().default(true).describe('Generate layout components'),
    includeGuards: z.boolean().default(true).describe('Include route guards for permissions'),
    generateRegistry: z.boolean().default(true).describe('Generate navRoutes.generated.ts'),
    dryRun: z.boolean().default(false).describe('Preview without writing files'),
  }).optional(),
});

export const ValidateFrontendRoutesInputSchema = z.object({
  path: z.string().optional().describe('Path to SmartStack project root (defaults to configured project path)'),
  scope: z.enum(['api-clients', 'routes', 'registry', 'all'])
    .default('all')
    .describe('Scope of validation'),
  options: z.object({
    fix: z.boolean().default(false).describe('Auto-fix minor issues'),
    strict: z.boolean().default(false).describe('Fail on warnings'),
  }).optional(),
});

export type ScaffoldApiClientInput = z.infer<typeof ScaffoldApiClientInputSchema>;
export type ScaffoldRoutesInput = z.infer<typeof ScaffoldRoutesInputSchema>;
export type ValidateFrontendRoutesInput = z.infer<typeof ValidateFrontendRoutesInputSchema>;

// ============================================================================
// Frontend Route Tool Result Types
// ============================================================================

export interface NavRouteEntry {
  navRoute: string;
  apiPath: string;
  webPath: string;
  permissions: string[];
  controller?: string;
  methods: string[];
}

export interface FrontendRouteValidationResult {
  valid: boolean;
  registry: {
    exists: boolean;
    routeCount: number;
    outdated: string[];
  };
  apiClients: {
    total: number;
    valid: number;
    issues: FrontendRouteIssue[];
  };
  routes: {
    total: number;
    orphaned: string[];
    missing: string[];
  };
  recommendations: string[];
}

export interface FrontendRouteIssue {
  type: 'missing-route' | 'invalid-path' | 'permission-mismatch' | 'orphaned-client';
  severity: 'error' | 'warning';
  file: string;
  navRoute?: string;
  message: string;
  suggestion: string;
}

// ============================================================================
// Frontend Extension Tool Schemas
// ============================================================================

export const SlotDefinitionSchema = z.object({
  name: z.string().describe('Slot name (e.g., "users.form.fields.after")'),
  location: z.string().describe('Where the slot is located in the page'),
  contextProps: z.array(z.string()).optional().describe('Props passed to slot content'),
});

export const ScaffoldFrontendExtensionInputSchema = z.object({
  type: z.enum(['infrastructure', 'page-slots', 'extension-example', 'all'])
    .describe('Type of extension to scaffold: infrastructure (types, contexts), page-slots (add slots to a page), extension-example (client usage example), all'),
  target: z.string().optional().describe('Target page or component (e.g., "UsersPage", "UserDetailPage")'),
  options: z.object({
    slots: z.array(SlotDefinitionSchema).optional().describe('Slot definitions to add'),
    outputPath: z.string().optional().describe('Custom output path'),
    dryRun: z.boolean().default(false).describe('Preview without writing files'),
    overwrite: z.boolean().default(false).describe('Overwrite existing files'),
  }).optional(),
});

export const AnalyzeExtensionPointsInputSchema = z.object({
  path: z.string().optional().describe('Path to SmartStack web project'),
  target: z.enum(['pages', 'components', 'forms', 'tables', 'all'])
    .default('all')
    .describe('Type of components to analyze'),
  filter: z.string().optional().describe('Filter by file name pattern (e.g., "User*")'),
});

export type SlotDefinition = z.infer<typeof SlotDefinitionSchema>;
export type ScaffoldFrontendExtensionInput = z.infer<typeof ScaffoldFrontendExtensionInputSchema>;
export type AnalyzeExtensionPointsInput = z.infer<typeof AnalyzeExtensionPointsInputSchema>;

// ============================================================================
// Frontend Extension Tool Result Types
// ============================================================================

export interface ExtensionPointAnalysis {
  file: string;
  componentName: string;
  type: 'page' | 'component' | 'form' | 'table';
  suggestedSlots: SuggestedSlot[];
  hasModal: boolean;
  hasForm: boolean;
  hasTable: boolean;
  imports: string[];
  lineCount: number;
}

export interface SuggestedSlot {
  name: string;
  location: string;
  description: string;
  contextProps: string[];
  priority: 'high' | 'medium' | 'low';
}

export interface ScaffoldFrontendExtensionResult {
  success: boolean;
  type: string;
  files: {
    path: string;
    content: string;
    action: 'created' | 'modified' | 'skipped';
  }[];
  instructions: string[];
  warnings: string[];
}

export interface AnalyzeExtensionPointsResult {
  totalFiles: number;
  analyzed: ExtensionPointAnalysis[];
  summary: {
    pagesWithForms: number;
    pagesWithTables: number;
    pagesWithModals: number;
    totalSuggestedSlots: number;
  };
  recommendations: string[];
}

// ============================================================================
// Hierarchy Analysis Tool Schemas
// ============================================================================

export const AnalyzeHierarchyPatternsInputSchema = z.object({
  path: z.string().optional().describe('Project path to analyze (default: SmartStack.app path)'),
  entityFilter: z.string().optional().describe('Filter entities by name pattern (e.g., "User*", "*Group*")'),
  includeRecommendations: z.boolean().default(true).describe('Include pattern recommendations for each hierarchy'),
  outputFormat: z.enum(['summary', 'detailed', 'json']).default('detailed').describe('Output format'),
});

export type AnalyzeHierarchyPatternsInput = z.infer<typeof AnalyzeHierarchyPatternsInputSchema>;

// ============================================================================
// Code Review Tool Input Schemas
// ============================================================================

export const ReviewCodeCheckSchema = z.enum([
  'security',
  'architecture',
  'hardcoded-values',
  'tests',
  'ai-hallucinations',
  'performance',
  'dead-code',
  'i18n',
  'accessibility',
  'all',
]);

export const ReviewCodeInputSchema = z.object({
  path: z.string().optional().describe('Project path to review (default: SmartStack.app path)'),
  scope: z.enum(['all', 'changed', 'staged']).default('all')
    .describe('Files to review: all, only changed (git diff), or staged files'),
  checks: z.array(ReviewCodeCheckSchema)
    .default(['all'])
    .describe('Categories of checks to run'),
  severity: z.enum(['blocking', 'critical', 'warning', 'all']).default('all')
    .describe('Filter results by minimum severity'),
});

export type ReviewCodeCheck = z.infer<typeof ReviewCodeCheckSchema>;
export type ReviewCodeInput = z.infer<typeof ReviewCodeInputSchema>;

// ============================================================================
// Hierarchy Analysis Tool Result Types
// ============================================================================

export interface EntityHierarchyNode {
  name: string;
  file: string;
  baseClass: string;
  isTenantAware: boolean;
  isSystemEntity: boolean;
  parent: string | null;
  children: string[];
  depth: number;
  isRoot: boolean;
  isLeaf: boolean;
  isSelfReferencing: boolean;
  selfReferenceField?: string;
  childCollections: string[];
  foreignKeys: {
    propertyName: string;
    referencedEntity: string;
    isNavigationProperty: boolean;
  }[];
  isHierarchical: boolean;
}

export interface HierarchyPatternRecommendation {
  entityName: string;
  currentPattern: 'self-referencing' | 'parent-child' | 'none';
  recommendedPattern: 'tvf-cte' | 'materialized-path' | 'simple-parentid' | 'nested-sets' | 'closure-table';
  reason: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  hierarchyDepth: number;
  estimatedNodes: number;
  sqlTemplate?: string;
  implementationSteps: string[];
}

export interface HierarchyAnalysisResult {
  totalEntities: number;
  hierarchicalEntities: number;
  selfReferencingEntities: number;
  maxDepth: number;
  hierarchies: EntityHierarchyNode[];
  recommendations: HierarchyPatternRecommendation[];
  circularDependencies: string[][];
}
